import { useEffect, useMemo, useState } from 'react';
import { AssetFormat, ContributionItem } from 'services/requests';
import {
  AssignDepositFormFieldNames,
  ContributionTypeOptions,
  defaultContributionType,
  useAssignDepositForm,
} from 'hooks/forms/useAssignDepositForm';
import {
  useAssignWalletTransfer,
  useGetUsersToAssignWalletTransfer,
} from 'hooks/api/contributions';
import { estCostBasisTransform, formatUSD } from 'utils';
import { mapIndependentIraTypeKeyToValue } from 'screens/IndependentIra/IraIndependentIra.utils';
import Button from 'components/Button';
import { Modal, ModalProps } from 'components/Modal';
import Field from 'components/Field';
import Picker from 'components/Picker';
import { showErrorToast, showInfoToast } from 'components/Toast';

import styles from './AssignDepositModal.module.css';
import messages from './AssignDepositModal.messages';

type AssignDepositModalProps = {
  onRequestClose: VoidFunction;
  contributionItemInfo: ContributionItem;
} & ModalProps;

const mapContributionTypeValueToKey = (type?: string) =>
  Object.entries(ContributionTypeOptions).find((item) => item[1] === type)?.[0] ?? '';

const AssignDepositModal = ({
  onRequestClose,
  contributionItemInfo,
  ...restOfProps
}: AssignDepositModalProps): JSX.Element | null => {
  const { data: users } = useGetUsersToAssignWalletTransfer(
    contributionItemInfo.externalId,
  );
  const { mutateAsync: assignWalletTransfer, isLoading: isAssignWalletTransferLoading } =
    useAssignWalletTransfer();

  const [isNextModalStateActivated, setIsNextModalStateActivated] = useState(false);
  const [contributionTypeSelected, setContributionTypeSelected] = useState<
    string | number | undefined
  >(defaultContributionType);
  const [userSelected, setUserSelected] = useState<string | number | undefined>();
  const [fiatEquivalentFocused, setFiatEquivalentFocused] = useState(false);

  const [usersOptions, usersOptionsKeys] = useMemo(() => {
    if (!users) return [new Map(), []];

    const mappedUsers = new Map(
      users.items.map((user) => [
        `#${user.accountNumber} (${user.firstName} ${
          user.lastName
        }, ${mapIndependentIraTypeKeyToValue(user.userProfileType)})`,
        user.id,
      ]),
    );
    const mappedUsersKeys = Array.from(mappedUsers.keys());

    return [mappedUsers, mappedUsersKeys];
  }, [users]);

  const selectedUserId = userSelected
    ? usersOptions.get(userSelected as string)
    : undefined;
  const selectedUserData = users?.items.find((user) => selectedUserId === user.id);
  const isActivePreNotifyDeposit = selectedUserData?.hasActivePreNotifyDeposit;

  const title = isNextModalStateActivated
    ? isActivePreNotifyDeposit
      ? messages.viewMode.warning.title
      : messages.viewMode.general.title
    : messages.editMode.title;
  const warningSubtitle = selectedUserData
    ? `${selectedUserData.firstName} ${selectedUserData.lastName} (#${selectedUserData.accountNumber}) ${messages.viewMode.warning.subtitle}`
    : messages.viewMode.general.subtitle;
  const subtitle = isNextModalStateActivated
    ? isActivePreNotifyDeposit
      ? warningSubtitle
      : messages.viewMode.general.subtitle
    : messages.editMode.subtitle;

  const form = useAssignDepositForm(usersOptionsKeys);

  const watchContributionTypeField = form.watch(
    AssignDepositFormFieldNames.contributionType,
  );
  const watchUserProfileIdField = form.watch(AssignDepositFormFieldNames.userProfileId);

  useEffect(() => {
    if (isNextModalStateActivated && isActivePreNotifyDeposit) {
      showErrorToast(messages.warningMessage);
    }
  }, [isNextModalStateActivated, isActivePreNotifyDeposit]);

  useEffect(() => {
    if (watchContributionTypeField === '') {
      setContributionTypeSelected(undefined);
    }
    if (
      Object.values(ContributionTypeOptions).find(
        (contributionType) => contributionType === watchContributionTypeField,
      )
    ) {
      setContributionTypeSelected(watchContributionTypeField);
    }
  }),
    [watchContributionTypeField];

  useEffect(() => {
    if (watchUserProfileIdField === '') {
      setUserSelected(undefined);
    }
    if (
      Object.values(usersOptionsKeys).find((user) => user === watchUserProfileIdField)
    ) {
      setUserSelected(watchUserProfileIdField);
    }
  }),
    [watchUserProfileIdField, usersOptionsKeys];

  const onCancelClick = (event: any) => {
    event.preventDefault();
    onRequestClose();
  };

  const onNextClick = () => {
    setIsNextModalStateActivated(true);
  };

  const onReturnClick = (event: any) => {
    event.preventDefault();
    setIsNextModalStateActivated(false);
  };

  const onSubmitClick = async (event: any): Promise<void> => {
    await assignWalletTransfer(
      {
        walletTransferExternalId: contributionItemInfo.externalId,
        contributionType: mapContributionTypeValueToKey(
          form.getValues(AssignDepositFormFieldNames.contributionType),
        ),
        userProfileId: selectedUserId,
        amount: contributionItemInfo.asset.amountStringified,
        fiatEquivalent:
          Number(form.getValues().fiatEquivalent) === 0
            ? undefined
            : Number(form.getValues().fiatEquivalent),
      },
      {
        onSuccess: () => {
          event.preventDefault();
          onRequestClose();
          showInfoToast(messages.confirmationMessage);
        },
      },
    );
  };

  return (
    <>
      <Modal
        {...restOfProps}
        onRequestClose={onRequestClose}
        className={styles.assignDepositModal}
      >
        <h3
          className={
            isNextModalStateActivated && isActivePreNotifyDeposit
              ? styles.titleWarning
              : styles.title
          }
        >
          {title}
        </h3>
        <p className={styles.subtitle}>{subtitle}</p>
        <form onSubmit={form.handleSubmit(onNextClick)}>
          <div className={styles.fieldWrapper}>
            <Field
              type={'text'}
              form={form}
              name={AssignDepositFormFieldNames.walletTransferExternalId}
              placeholder={messages.placeholder.transferId}
              defaultValue={contributionItemInfo.externalId}
              className={styles.textField}
              disabled={true}
            />
            <Picker
              form={form}
              name={AssignDepositFormFieldNames.contributionType}
              options={Object.values(ContributionTypeOptions)}
              placeholder={messages.placeholder.contributionType}
              onSelect={setContributionTypeSelected}
              selectedOption={contributionTypeSelected}
              size={'large'}
              disabled={isNextModalStateActivated}
            />
            <Picker
              form={form}
              name={AssignDepositFormFieldNames.userProfileId}
              options={usersOptionsKeys}
              placeholder={messages.placeholder.user}
              onSelect={setUserSelected}
              selectedOption={userSelected}
              size={'small'}
              disabled={isNextModalStateActivated}
            />
            <Field
              type={'text'}
              form={form}
              name={AssignDepositFormFieldNames.amount}
              placeholder={messages.placeholder.amount}
              defaultValue={
                contributionItemInfo.asset.assetFormat === AssetFormat.FIAT
                  ? formatUSD(contributionItemInfo.asset.amount)
                  : `${contributionItemInfo.asset.amountStringified} ${contributionItemInfo.asset.code}`
              }
              className={styles.textField}
              disabled={true}
            />
            {contributionItemInfo.asset.assetFormat !== AssetFormat.FIAT && (
              <Field
                type={'text'}
                form={form}
                name={AssignDepositFormFieldNames.fiatEquivalent}
                placeholder={messages.placeholder.fiatEquivalent}
                className={styles.fiatEquivalentTextField}
                onFocus={() => setFiatEquivalentFocused(true)}
                onBlur={() => setFiatEquivalentFocused(false)}
                mask={(value) =>
                  estCostBasisTransform.mask(value, {
                    ignoreEmpty: fiatEquivalentFocused,
                  })
                }
                mapper={estCostBasisTransform.mapper}
                disabled={isNextModalStateActivated}
              />
            )}
            <div className={styles.buttonWrapper}>
              <Button
                variant={'outline'}
                type={'button'}
                onClick={!isNextModalStateActivated ? onCancelClick : onReturnClick}
              >
                {!isNextModalStateActivated
                  ? messages.editMode.buttonCancel
                  : messages.viewMode.general.buttonReturn}
              </Button>

              <Button
                onClick={isNextModalStateActivated ? onSubmitClick : undefined}
                isLoading={isAssignWalletTransferLoading}
              >
                {!isNextModalStateActivated
                  ? messages.editMode.buttonNext
                  : isActivePreNotifyDeposit
                  ? messages.viewMode.warning.buttonSubmit
                  : messages.viewMode.general.buttonSubmit}
              </Button>
            </div>
          </div>
        </form>
      </Modal>
    </>
  );
};

export default AssignDepositModal;
